/* *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2025 - Koninklijk Nederlands Meteorologisch Instituut (KNMI)
 * Copyright 2025 - Finnish Meteorological Institute (FMI)
 * Copyright 2025 - The Norwegian Meteorological Institute (MET Norway)
 * */

import { Box, IconButton } from '@mui/material';
import React from 'react';
import type { ItemInterface } from 'react-sortablejs';
import { ReactSortable } from 'react-sortablejs';
import { DragHandle } from '@opengeoweb/theme';
import { ResizablePanelStandalone } from '../ResizablePanel/ResizablePanelStandalone';
import type { ResizablePanelComponent } from '../ResizablePanel/ResizablePanelStandalone';

export interface SortablePanelItem {
  id: string;
  name: string;
  isDisplayed: boolean;
  minWidth?: number;
  maxWidth?: number;
  headerAction?: React.ReactElement;
  render: () => React.ReactNode;
  panelRef?: React.Ref<HTMLDivElement>;
  onOpenChange?: (isOpen: boolean) => void;
  onResizeStart?: () => void;
  onResizeEnd?: () => void;
}

export interface SortablePanelsProps {
  panels: SortablePanelItem[];
  onOrderChange: (
    newOrder: { id: string; name: string; isDisplayed: boolean }[],
  ) => void;
  panelComponent?: ResizablePanelComponent;
  /**
   * Optional. Forwarded to each rendered panel component. Redux-aware panel
   * components use it to scope their external state by parent view.
   */
  viewId?: string;
}

interface SortableItem extends ItemInterface {
  id: string;
  name: string;
  isDisplayed: boolean;
}

export const SortablePanels = ({
  panels,
  onOrderChange,
  panelComponent: PanelComponent = ResizablePanelStandalone,
  viewId,
}: SortablePanelsProps): React.ReactElement => {
  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'row',
        width: '100%',
        height: '100%',
        padding: 1,
      }}
    >
      <ReactSortable<SortableItem>
        animation={200}
        direction="horizontal"
        draggable=".sortable-item"
        handle=".drag-handle"
        list={panels.map(({ id, name, isDisplayed }) => ({
          id,
          name,
          isDisplayed,
        }))}
        setList={(list) =>
          onOrderChange(
            list.map(({ id, name, isDisplayed }) => ({
              id,
              name,
              isDisplayed,
            })),
          )
        }
        style={{
          display: 'flex',
          gap: 4,
          width: '100%',
        }}
        tag="div"
      >
        {panels.map((panel) => (
          <PanelComponent
            handleButtonComponent={
              <IconButton
                aria-label="drag-handle"
                className="drag-handle"
                size="small"
                sx={{ width: '24px', height: '24px' }}
              >
                <DragHandle fontSize="small" />
              </IconButton>
            }
            headerAction={panel.headerAction}
            isDisplayed={panel.isDisplayed}
            key={panel.id}
            maxWidth={panel.maxWidth}
            minWidth={panel.minWidth}
            name={panel.name}
            onOpenChange={panel.onOpenChange}
            onResizeEnd={panel.onResizeEnd}
            onResizeStart={panel.onResizeStart}
            panelId={panel.id}
            ref={panel.panelRef}
            viewId={viewId}
          >
            {panel.render()}
          </PanelComponent>
        ))}
      </ReactSortable>
    </Box>
  );
};
